import { useDatabaseContent } from '@databases/database-state';
import { defaultStoreUtils } from '@databases/redux';
import { selectHasSelectedCells } from '@databases/redux/cellSelections/selectors';
import { useCallback, useEffect } from 'react';

import { doExportPasteContent } from '../export';

interface UseCopyDatabaseContentProps {
  contentId: string;
}

/**
 * Registers a document event listener that reacts to copy events.
 * This is used to copy the selected database content to the clipboard.
 */
export const useCopyListener = ({ contentId }: UseCopyDatabaseContentProps) => {
  const store = defaultStoreUtils.useStore();
  const { title: databaseName } = useDatabaseContent();

  const hasSelection = defaultStoreUtils.useEqualSelector((state) => {
    return selectHasSelectedCells(state, {
      contentId,
    });
  });

  const handleExportDatabaseContent = useCallback(() => {
    doExportPasteContent(contentId, databaseName || '', store.getState());
  }, [contentId, databaseName, store]);

  useEffect(() => {
    const copyListener = (e: ClipboardEvent) => {
      if (getSelection()?.type !== 'Range') {
        e.preventDefault();
        handleExportDatabaseContent();
      }
    };

    if (!hasSelection) {
      return;
    }
    document.addEventListener('copy', copyListener);
    return () => {
      document.removeEventListener('copy', copyListener);
    };
  }, [handleExportDatabaseContent, hasSelection]);
};
